import DocumentDO = App.Data.Explorer.DocumentDO;
import QuickFilterNodeValuesDTO = App.Data.Explorer.Filter.QuickFilterNodeValuesDTO;
import { activeFiltersWithoutDisplayOnlyFields } from "@/modules/explorer/composables/filterDisplaySettings";
import { DISPLAY_ONLY_SETTINGS } from "@/modules/explorer/composables/dataTableDisplaySettings";
import { GRAPH_DISPLAY_ONLY_FIELDS } from "@/modules/explorer/composables/graphDisplaySettings";

/** The comparison label used for budget comparison — see DocumentDateComparisonDTO::getComparisonPeriodLabels(). */
export const BUDGET_COMPARISON_LABEL = "Bud";

/**
 * Every comparison label that appears in at least one row of a quick filter values response.
 * Used to tell apart "the backend never returned this comparison for the whole document"
 * (e.g. budget comparison isn't supported for this dimension, see
 * QuickFilterService::isBudgetComparisonSupportedForDimension) from "this specific row
 * legitimately has 0/no data for this comparison".
 */
export const getRespondedComparisonLabels = (
	response: Record<string, QuickFilterNodeValuesDTO>
): Set<string> => {
	const labels = new Set<string>();

	Object.values(response).forEach((entry) => {
		Object.keys(entry.comparisonValues ?? {}).forEach((label) =>
			labels.add(label)
		);
	});

	return labels;
};

/**
 * Comparison labels that should render as "n/a" rather than 0: only budget comparison can be
 * genuinely unsupported for a dimension (it may live in a coarser-grained table — see
 * QuickFilterService::isBudgetComparisonSupportedForDimension). Period comparisons (Y-n, custom
 * periods) are always computable, so a label missing from every row there just means a
 * legitimate 0, never "n/a".
 */
export const getUnavailableComparisonLabels = (
	comparisonLabels: string[],
	response: Record<string, QuickFilterNodeValuesDTO>
): Set<string> => {
	const respondedLabels = getRespondedComparisonLabels(response);

	return new Set(
		comparisonLabels.filter(
			(label) =>
				label === BUDGET_COMPARISON_LABEL && !respondedLabels.has(label)
		)
	);
};

export type ComparisonCellState =
	| { status: "loading" }
	| { status: "unavailable" }
	| { status: "value"; value: number };

/**
 * Decides what a quick filter comparison cell (Default or Hierarchy) should render:
 *  - "loading" while the row, or the comparison label itself, hasn't been fetched yet
 *    (e.g. a comparison period was just added and hasn't come back from the backend)
 *  - "unavailable" when the label is configured on the document but the backend never
 *    returned it for any row of the last completed fetch
 *  - otherwise the actual (possibly legitimately zero) value
 */
export const getComparisonCellState = (
	item: { value: number | null; comparisonValues?: Record<string, number> },
	label: string,
	loadedLabels: Set<string>,
	unavailableLabels: Set<string>
): ComparisonCellState => {
	if (item.value === null || !loadedLabels.has(label)) {
		return { status: "loading" };
	}
	if (unavailableLabels.has(label)) {
		return { status: "unavailable" };
	}
	return { status: "value", value: item.comparisonValues?.[label] ?? 0 };
};

export const hasDocumentChanged = (
	document: DocumentDO,
	prevDocument: DocumentDO,
	dimensionId: number
): boolean => {
	const stripDocument = (document: DocumentDO): DocumentDO => {
		const stripped: Partial<DocumentDO> = {
			...document,
			active_filters: activeFiltersWithoutDisplayOnlyFields(
				document.active_filters?.filter(
					(filter) => filter.dimension.id !== dimensionId
				)
			),
			data_tables: document.data_tables?.map((dataTable) => ({
				...dataTable,
				...DISPLAY_ONLY_SETTINGS,
			})),
			uuid: undefined,
			parent_uuid: undefined,
		};

		for (const field of GRAPH_DISPLAY_ONLY_FIELDS) {
			delete stripped[field];
		}

		return stripped as DocumentDO;
	};

	const newDocument = stripDocument(document);
	const oldDocument = stripDocument(prevDocument);

	return JSON.stringify(newDocument) !== JSON.stringify(oldDocument);
};
